import React, { useState, useEffect, useRef  } from 'react';
import { motion } from 'framer-motion';
import { IoEyeOutline, IoFilterOutline, IoCheckmarkCircleOutline, IoCloseCircleOutline } from 'react-icons/io5';
import { BsCalendar4 } from "react-icons/bs";
import { useUser } from '../../context/UserContext';
import { useNavigate } from 'react-router-dom';
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import { toast } from 'react-toastify';
import { usePendingCompOffRequests } from '../../context/PendingCompOffRequestContext';
import api from '../../utils/api';
import { formatDate, formatTiming } from '../../utils/format';
import { LuClipboardPlus } from "react-icons/lu";
import { IoFilterSharp } from "react-icons/io5";

const statusConfig = {
  APPROVED: { bg:"bg-emerald-50",text:"text-emerald-700",dot:"bg-emerald-500",border:"border-emerald-200",label:"Approved" },
  PENDING:  { bg:"bg-amber-50",  text:"text-amber-700",  dot:"bg-amber-400",  border:"border-amber-200",  label:"Pending"  },
  REJECTED: { bg:"bg-red-50",    text:"text-red-700",    dot:"bg-red-500",    border:"border-red-200",    label:"Rejected" },
};
const sessionColor = { FULL_DAY:"bg-blue-100 text-blue-700", FIRST_HALF:"bg-violet-100 text-violet-700", SECOND_HALF:"bg-indigo-100 text-indigo-700" };
const sessionAbbr  = { FULL_DAY:"FD", FIRST_HALF:"FH", SECOND_HALF:"SH" };
const sessionLabel = (t) => t==="FULL_DAY"?"Full Day":t==="FIRST_HALF"?"First Half":"Second Half";
const avatarColors = ["bg-indigo-100 text-indigo-700","bg-pink-100 text-pink-700","bg-teal-100 text-teal-700","bg-amber-100 text-amber-700","bg-cyan-100 text-cyan-700"];
const getInitials  = (n="") => n.split(' ').map(w=>w[0]).slice(0,2).join('').toUpperCase();
const avatarColor  = (n="") => avatarColors[n.charCodeAt(0)%avatarColors.length];

function ManagerCompOffRequests() {
  const navigate = useNavigate();
  const { currentUser, loading } = useUser();
  const [compOffRequests, setCompOffRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [rejectComment, setRejectComment] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [apiLoading, setApiLoading] = useState(true);
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [selectedRequests, setSelectedRequests] = useState([]);
  const [showBulkRejectModal, setShowBulkRejectModal] = useState(false);
  const [bulkRejectComment, setBulkRejectComment] = useState('');
  const [isBulkApproving, setIsBulkApproving] = useState(false);
  const [isBulkRejecting, setIsBulkRejecting] = useState(false);
  const requestsPerPage = 6;
  const { updatePendingCompOffCount } = usePendingCompOffRequests();

    // ── Filter states (applied) ──────────────────────────────────────────────
  const [status, setStatus] = useState('ALL');
  const [selectedManager, setSelectedManager] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');

    // ── Draft states (inside panel, before Apply) ────────────────────────────
  const [draftStatus, setDraftStatus] = useState('ALL');
  const [draftManager, setDraftManager] = useState('');
  const [draftLocation, setDraftLocation] = useState('');

  const [filterOpen, setFilterOpen] = useState(false);
  const filterRef = useRef(null);
  const [managers, setManagers] = useState([]);


  // Role-based dynamic configuration
  const isCEO = currentUser?.role === "CEO";
  const roleKey = isCEO ? "ceoId" : "managerId";
  const pageTitle = isCEO ? "Company Comp Off Requests" : "Team Comp Off Requests";

  // Active filter badge count
  const activeFilterCount = isCEO
    ? [
        status !== 'ALL' ? status : '',
        selectedManager,
        selectedLocation,
      ].filter(Boolean).length
    : [
        status !== 'ALL' ? status : '',
        selectedLocation,
      ].filter(Boolean).length;


  // ── Data fetching ────────────────────────────────────────────────────────
  const fetchCompOffRequests = async () => {
    if (!currentUser?.id) return;
    setApiLoading(true);
    try {
      const response = await api.get(`/api/compoffs/recent?${roleKey}=${currentUser?.id}`);
      setCompOffRequests(response.data.map(req => ({
        id: req.id,
        employeeName: req.employeeName,
        employeeId: req.employeeId,
        compOffDate: req.onDate,
        reason: req.reason,
        timing: req.timing,
        status: req.comOffStatus,
        approverComment: req.approverComment,
        location: req.location,
        managerId: req.managerId,
        createdAt: req.createdAt,
        updatedAt: req.updatedAt,
      })));
    } catch (e) { console.error(e); } finally { setApiLoading(false); }
  };

  const fetchManagers = async () => {
    try {
      const res = await api.get('/auth/managers');
      setManagers(res.data || []);
    } catch (e) { console.error('Failed to fetch managers', e); }
  };

  useEffect(() => { if (currentUser?.id) fetchCompOffRequests(); }, [currentUser?.id]);
  useEffect(() => { fetchManagers(); }, []);
  useEffect(() => {
    if (!loading) {
      if (!currentUser) { navigate('/login'); return; }
      if (!['MANAGER', 'CEO'].includes(currentUser.role)) navigate('/unauthorized');
    }
  }, [currentUser, loading, navigate]);

  // Close filter panel on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) {
        setFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  
  const filteredRequests = compOffRequests.filter(r => {
    if (status !== 'ALL' && r.status !== status) return false;
    if (selectedLocation && r.location && r.location !== selectedLocation) return false;
    if (isCEO && selectedManager && String(r.managerId) !== String(selectedManager)) return false;
    return true;
  });

  const totalPages = Math.ceil(filteredRequests.length / requestsPerPage);
  const paginatedRequests = filteredRequests.slice(
    (currentPage - 1) * requestsPerPage, currentPage * requestsPerPage
  );

  const handleView = (r) => { setSelectedRequest(r); setShowViewModal(true); };

  const fetchPendingCount = async () => {
    try {
      const r = await api.get(`/api/compoffs/pending-count?${roleKey}=${currentUser?.id}`);
      updatePendingCompOffCount(r.data);
    } catch(e) {}
  };

  const handleApprove = async (id) => {
    setIsApproving(true);
    try {
      await api.put(`/api/compoffs/approve/${id}`, null, { params: { [roleKey]: currentUser?.id, comment: '' } });
      await fetchPendingCount(); await fetchCompOffRequests();
      setShowViewModal(false); toast.success('Comp off request approved');
    } catch(e) { toast.error('Approval failed'); } finally { setIsApproving(false); }
  };

  const handleReject = async (id) => {
    if (!rejectComment.trim()) { toast.error('Please provide a reason'); return; }
    setIsRejecting(true);
    try {
      await api.put(`/api/compoffs/reject/${id}`, null, { params: { [roleKey]: currentUser?.id, comment: rejectComment } });
      await fetchPendingCount(); await fetchCompOffRequests();
      setShowViewModal(false); setRejectComment(''); toast.success('Comp off request rejected');
    } catch(e) { toast.error('Rejection failed'); } finally { setIsRejecting(false); }
  };

  const handleSelectRequest = (id) => setSelectedRequests(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const handleSelectAll = () => {
    const ids = paginatedRequests.map(r => r.id);
    const all = ids.every(id => selectedRequests.includes(id));
    setSelectedRequests(all ? prev => prev.filter(id => !ids.includes(id)) : prev => [...new Set([...prev, ...ids])]);
  };



  const handleBulkApprove = async () => {
    if (!selectedRequests.length) return;
    setIsBulkApproving(true);
    try { const res=await Promise.allSettled(selectedRequests.map(id=>api.put(`/api/compoffs/approve/${id}`,null,{params:{[roleKey]:currentUser?.id,comment:''}}))); const ok=res.filter(r=>r.status==='fulfilled').length; const fail=res.filter(r=>r.status==='rejected').length; if(ok)toast.success(`${ok} approved`); if(fail)toast.error(`${fail} failed`); await fetchPendingCount(); await fetchCompOffRequests(); setSelectedRequests([]); } catch(e){ toast.error('Bulk approval failed'); } finally { setIsBulkApproving(false); }
  };
  const handleBulkReject = async () => {
    if (!bulkRejectComment.trim()) { toast.error('Please provide a reason'); return; }
    setIsBulkRejecting(true);
    try { const res=await Promise.allSettled(selectedRequests.map(id=>api.put(`/api/compoffs/reject/${id}`,null,{params:{[roleKey]:currentUser?.id,comment:bulkRejectComment}}))); const ok=res.filter(r=>r.status==='fulfilled').length; const fail=res.filter(r=>r.status==='rejected').length; if(ok)toast.success(`${ok} rejected`); if(fail)toast.error(`${fail} failed`); await fetchPendingCount(); await fetchCompOffRequests(); setSelectedRequests([]); setShowBulkRejectModal(false); setBulkRejectComment(''); } catch(e){ toast.error('Bulk rejection failed'); } finally { setIsBulkRejecting(false); }
  };

  const isActionInProgress = isApproving || isRejecting;
  const isBulkActionInProgress = isBulkApproving || isBulkRejecting;

  if (loading || !currentUser) return <div className="p-4 sm:p-6 w-full mx-auto"><div className="flex justify-center items-center h-[400px]"><svg className="animate-spin h-8 w-8 text-slate-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path></svg></div></div>;

  return (
    <div className="sm:p-6 sm:mt-auto mt-8 w-full mx-auto">
      <motion.div className="flex justify-between items-center mb-6 bg-slate-200 rounded-2xl p-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <div className='flex items-center gap-4 text-xl xs:text-2xl font-bold truncate'>
          <div className='p-2 rounded-xl bg-slate-700 text-slate-100'><LuClipboardPlus className="text-2xl" /></div>
          <h2 className="text-xl xs:text-2xl font-bold">{pageTitle}</h2>
        </div>
         <div className="relative" ref={filterRef}>
          <button
            onClick={() => {
              if (!filterOpen) {
                setDraftStatus(status);
                setDraftManager(selectedManager);
                setDraftLocation(selectedLocation);
              }
              setFilterOpen(prev => !prev);
            }}
            className={`relative flex items-center gap-1.5 px-4 py-1.5 rounded-full border text-sm font-medium transition
              ${activeFilterCount > 0
                ? 'bg-slate-700 text-white border-slate-700'
                : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
              }`}
          >
            <IoFilterSharp className="text-base" />
            <span>Filter</span>
            {activeFilterCount > 0 && (
              <span className="ml-1 bg-white text-slate-700 text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                {activeFilterCount}
              </span>
            )}
          </button>

          {filterOpen && (
            <div className="absolute right-0 mt-2 w-72 bg-white border border-slate-200 rounded-2xl shadow-xl z-50 p-4 space-y-4">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-semibold text-slate-700">Filters</span>
                {activeFilterCount > 0 && (
                  <button
                    onClick={() => {
                      setDraftStatus('ALL'); setDraftManager(''); setDraftLocation('');
                      setStatus('ALL'); setSelectedManager(''); setSelectedLocation('');
                    }}
                    className="text-xs text-red-500 hover:text-red-600 font-medium"
                  >Clear all</button>
                )}
              </div>

              {/* Status — both CEO and Manager */}
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1.5">Status</label>
                <select
                  value={draftStatus}
                  onChange={(e) => setDraftStatus(e.target.value)}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none"
                >
                  <option value="ALL">All Status</option>
                  <option value="APPROVED">Approved</option>
                  <option value="PENDING">Pending</option>
                  <option value="REJECTED">Rejected</option>
                </select>
              </div>

              {/* Manager — CEO only */}
              {isCEO && (
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1.5">Manager</label>
                  <select
                    value={draftManager}
                    onChange={(e) => setDraftManager(e.target.value)}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none"
                  >
                    <option value="">All Managers</option>
                    {managers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                  </select>
                </div>
              )}

              {/* Location — both CEO and Manager */}
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1.5">Location</label>
                <div className="flex gap-2">
                  {['Bengaluru', 'Mangaluru'].map(loc => (
                    <button
                      key={loc}
                      onClick={() => setDraftLocation(prev => prev === loc ? '' : loc)}
                      className={`flex-1 py-1.5 rounded-lg text-xs font-medium border transition
                        ${draftLocation === loc
                          ? 'bg-slate-700 text-white border-slate-700'
                          : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}
                    >{loc}</button>
                  ))}
                </div>
              </div>

              <button
                onClick={() => {
                  setStatus(draftStatus);
                  setSelectedLocation(draftLocation);
                  if (isCEO) setSelectedManager(draftManager);
                  setCurrentPage(1);
                  setFilterOpen(false);
                }}
                className="w-full bg-slate-700 text-white py-2 rounded-lg text-sm font-medium hover:bg-slate-600 transition"
              >Apply Filters</button>
            </div>
          )}
        </div>
      </motion.div>

      {/* ── Bulk Action Bar ── */}
      {selectedRequests.length > 0 && (
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4 flex justify-between items-center">
          <span className="font-semibold text-blue-800">{selectedRequests.length} request(s) selected</span>
          <div className="flex gap-3">
            <button onClick={handleBulkApprove} disabled={isBulkActionInProgress} className={`px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2 ${isBulkActionInProgress?'opacity-60 cursor-not-allowed':''}`}><IoCheckmarkCircleOutline className="text-lg" /><span>Approve All</span></button>
            <button onClick={() => setShowBulkRejectModal(true)} disabled={isBulkActionInProgress} className={`px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2 ${isBulkActionInProgress?'opacity-60 cursor-not-allowed':''}`}><IoCloseCircleOutline className="text-lg" /><span>Reject All</span></button>
            <button onClick={() => setSelectedRequests([])} className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300">Clear</button>
          </div>
        </motion.div>
      )}

      {/* ── Bulk Reject Modal ── */}
      {showBulkRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div className="bg-white p-6 rounded-2xl shadow-xl max-w-md w-full mx-4" initial={{ opacity:0, scale:0.9 }} animate={{ opacity:1, scale:1 }}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-slate-800">Reject Multiple Requests</h3>
              <button onClick={() => { setShowBulkRejectModal(false); setBulkRejectComment(''); }} className="text-slate-400 hover:text-slate-600">✕</button>
            </div>
            <div className="space-y-4">
              <p className="text-sm text-slate-600">You are about to reject <strong>{selectedRequests.length}</strong> request(s).</p>
              <textarea placeholder="Enter reason for rejection..." value={bulkRejectComment} onChange={(e) => setBulkRejectComment(e.target.value)} className="w-full p-3 border border-slate-200 rounded-lg text-sm" rows={4} autoFocus />
              <div className="flex justify-end gap-3">
                <button onClick={() => { setShowBulkRejectModal(false); setBulkRejectComment(''); }} className="px-4 py-2 text-sm font-medium text-slate-600" disabled={isBulkRejecting}>Cancel</button>
                <button onClick={handleBulkReject} disabled={isBulkRejecting || !bulkRejectComment.trim()} className={`px-4 py-2 text-sm font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 ${isBulkRejecting||!bulkRejectComment.trim()?'opacity-60 cursor-not-allowed':''}`}>
                  {isBulkRejecting ? "Rejecting..." : "Reject All"}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* ── View Modal ── */}
      {showViewModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" style={{ pointerEvents: isActionInProgress ? 'none' : 'auto' }}>
          <motion.div className="bg-white p-6 rounded-2xl shadow-xl max-w-md w-full mx-4" initial={{ opacity:0, scale:0.9 }} animate={{ opacity:1, scale:1 }}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-slate-800">Comp Off Request Details</h3>
              <button onClick={() => setShowViewModal(false)} className="text-slate-400 hover:text-slate-600">✕</button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-sm text-slate-500">Employee Name</p><p className="font-medium text-slate-800">{selectedRequest.employeeName}</p></div>
                <div><p className="text-sm text-slate-500">Comp Off Date</p><p className="font-medium text-slate-800">{formatDate(selectedRequest.compOffDate)}</p></div>
              </div>
              <div><p className="text-sm text-slate-500">Reason</p><p className="font-medium text-slate-800">{selectedRequest.reason}</p></div>
              <div className="grid grid-cols-2 gap-4">
  <div>
    <p className="text-sm text-slate-500">Applied On</p>
    <p className="font-medium text-slate-800">
      {selectedRequest.createdAt ? new Date(selectedRequest.createdAt).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : '—'}
    </p>
  </div>
  <div>
    <p className="text-sm text-slate-500">
      {selectedRequest.status?.toUpperCase() === 'APPROVED' ? 'Approved Date' : selectedRequest.status?.toUpperCase() === 'REJECTED' ? 'Rejected Date' : 'Edited Date'}
    </p>
    <p className="font-medium text-slate-800">
      {selectedRequest.updatedAt ? new Date(selectedRequest.updatedAt).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : '—'}
    </p>
  </div>
</div>
              <div className="flex flex-col gap-3 pt-4">
                <div className="flex items-center gap-2 text-sm text-slate-600"><span>Note:</span><span className="italic">Comment is required only for rejection</span></div>
                <textarea placeholder="Enter reason for rejection..." value={rejectComment} onChange={(e) => setRejectComment(e.target.value)} className="w-full p-3 border border-slate-200 rounded-lg text-sm" rows={3} />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                {selectedRequest.status?.toLowerCase() === "pending" && (
                  <>
                    <button onClick={() => handleApprove(selectedRequest.id)} disabled={isApproving} className={`px-4 py-2 text-sm font-medium text-green-600 hover:text-green-800 flex items-center gap-2 ${isApproving?'opacity-60 cursor-not-allowed':''}`}>{isApproving?"Approving...":"Approve"}</button>
                    <button onClick={() => handleReject(selectedRequest.id)} disabled={isRejecting} className={`px-4 py-2 text-sm font-medium text-red-600 hover:text-red-800 flex items-center gap-2 ${isRejecting?'opacity-60 cursor-not-allowed':''}`}>{isRejecting?"Rejecting...":"Reject"}</button>
                  </>
                )}
                {selectedRequest.status?.toLowerCase() === "approved" && (
                  <>
                    <button disabled className="px-4 py-2 text-sm font-medium text-green-300 cursor-not-allowed">Approve</button>
                    <button onClick={() => handleReject(selectedRequest.id)} disabled={isRejecting} className={`px-4 py-2 text-sm font-medium text-red-600 hover:text-red-800 flex items-center gap-2 ${isRejecting?'opacity-60 cursor-not-allowed':''}`}>{isRejecting?"Rejecting...":"Reject"}</button>
                  </>
                )}
                {selectedRequest.status?.toLowerCase() === "rejected" && (
                  <>
                    <button onClick={() => handleApprove(selectedRequest.id)} disabled={isApproving} className={`px-4 py-2 text-sm font-medium text-green-600 hover:text-green-800 flex items-center gap-2 ${isApproving?'opacity-60 cursor-not-allowed':''}`}>{isApproving?"Reapproving...":"Reapprove"}</button>
                    <button disabled className="px-4 py-2 text-sm font-medium text-red-300 cursor-not-allowed">Reject</button>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* ── Desktop Table ── */}
      <motion.div className="hidden sm:block bg-white rounded-2xl shadow-md overflow-hidden" transition={{ duration: 0.5 }}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-slate-100 text-left text-slate-600">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">SL No</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">Employee ID</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">Employee Name</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">Comp Off Date</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">Timing</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">Status</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {apiLoading ? (
                <tr><td colSpan="7" className="px-6 py-8 text-center">
                  <div className="flex justify-center">
                    <svg className="animate-spin h-8 w-8 text-slate-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                    </svg>
                  </div>
                </td></tr>
              ) : paginatedRequests.length > 0 ? (
                paginatedRequests.map((r, i) => (
                  <tr key={r.id} className="hover:bg-slate-50/50 transition-colors duration-150">
                    <td className="px-6 py-4 text-sm text-slate-800 font-medium">{(currentPage-1)*requestsPerPage+i+1}</td>
                    <td className="px-6 py-4 text-sm text-slate-800 font-medium">{r.employeeId}</td>
                    <td className="px-6 py-4 text-sm text-slate-800 font-medium">{r.employeeName}</td>
                    <td className="px-6 py-4 text-sm text-slate-800 font-medium">{formatDate(r.compOffDate)}</td>
                    <td className="px-6 py-4 text-sm text-slate-800 font-medium">{formatTiming(r.timing)}</td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${r.status?.toLowerCase()==="approved"?"bg-green-100 text-green-800":r.status?.toLowerCase()==="pending"?"bg-yellow-100 text-yellow-800":"bg-red-100 text-red-800"}`}>
                        {r.status ? r.status.charAt(0).toUpperCase()+r.status.slice(1).toLowerCase() : ""}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-center">
                        <motion.button onClick={() => handleView(r)} className="text-blue-600 hover:text-blue-800 transition-colors" whileHover={{ scale:1.1 }} whileTap={{ scale:0.9 }} title="View Details">
                          <IoEyeOutline className="text-xl" />
                        </motion.button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan="7" className="px-6 py-8 text-center text-slate-500">No comp off requests found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="px-6 py-4 border-t border-slate-100">
          <Stack spacing={2} className="flex justify-end">
            <Pagination count={totalPages} page={currentPage} onChange={(_,p) => setCurrentPage(p)} variant="outlined" shape="rounded-full" size="large" showFirstButton showLastButton sx={{"& .MuiPaginationItem-root":{color:"#475569",borderColor:"#e2e8f0"},"& .Mui-selected":{backgroundColor:"#475569 !important",color:"#fff"}}} />
          </Stack>
        </div>
      </motion.div>

      {/* ── Mobile View ── */}
      <div className="sm:hidden space-y-3">
        {apiLoading ? (
          [1,2,3].map(i => (
            <div key={i} className="bg-white p-4 rounded-2xl border border-slate-100 animate-pulse">
              <div className="flex gap-3 mb-3">
                <div className="w-9 h-9 bg-slate-200 rounded-xl"></div>
                <div className="space-y-2 flex-1"><div className="h-4 w-28 bg-slate-200 rounded"></div><div className="h-3 w-20 bg-slate-200 rounded"></div></div>
                <div className="h-6 w-16 bg-slate-200 rounded-full"></div>
              </div>
              <div className="h-px bg-slate-100 my-2"></div>
              <div className="h-3 w-40 bg-slate-200 rounded"></div>
            </div>
          ))
        ) : paginatedRequests.length > 0 ? (
          paginatedRequests.map((request, index) => {
            const st = statusConfig[request.status] || statusConfig["PENDING"];
            const sColor = sessionColor[request.timing] || "bg-slate-100 text-slate-600";
            const initials = getInitials(request.employeeName);
            const aColor = avatarColor(request.employeeName);
            return (
              <motion.div key={request.id} onClick={() => handleView(request)} className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden active:scale-[0.99] transition-transform cursor-pointer" initial={{ opacity:0, y:8 }} animate={{ opacity:1, y:0 }} transition={{ duration:0.2, delay:index*0.04 }}>
                <div className="p-4">
                  <div className="flex items-start gap-2">
                    <div onClick={e => e.stopPropagation()} className="pt-0.5">
                      <input type="checkbox" checked={selectedRequests.includes(request.id)} onChange={() => handleSelectRequest(request.id)} className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500" />
                    </div>
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-[11px] font-bold shrink-0 ${aColor}`}>{initials}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-semibold text-slate-800 leading-tight truncate">{request.employeeName}</p>
                      <span className={`inline-block mt-0.5 text-[11px] font-medium px-2 py-0.5 rounded-full ${sColor}`}>{sessionLabel(request.timing)}</span>
                    </div>
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border shrink-0 ${st.bg} ${st.text} ${st.border}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`}></span>{st.label}
                    </span>
                  </div>
                  <div className="h-px bg-slate-100 my-3" />
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5 text-[12px] text-slate-500">
                      <BsCalendar4 className="text-slate-400 shrink-0" />
                      <span className="font-medium text-slate-700">{formatDate(request.compOffDate)}</span>
                    </div>
                    <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${sColor}`}>{sessionAbbr[request.timing]}</span>
                  </div>
                  {request.reason && (
                    <p className="mt-2.5 text-[12px] text-slate-400 line-clamp-1">
                      <span className="font-medium text-slate-500">Reason: </span>{request.reason}
                    </p>
                  )}
                </div>
              </motion.div>
            );
          })
        ) : (
          <div className="bg-white rounded-2xl border border-dashed border-slate-200 p-10 text-center">
            <LuClipboardPlus className="text-4xl text-slate-300 mx-auto mb-2" />
            <p className="text-sm font-medium text-slate-400">No comp off requests found.</p>
          </div>
        )}

        {totalPages > 1 && (
          <div className="mt-4 flex items-center justify-between px-1">
            <span className="text-[12px] text-slate-400 font-medium">Page <span className="text-slate-600 font-semibold">{currentPage}</span> of {totalPages}</span>
            <div className="flex items-center gap-1.5">
              <motion.button onClick={() => setCurrentPage(p => Math.max(1, p-1))} disabled={currentPage===1} className={`w-8 h-8 flex items-center justify-center rounded-xl border text-sm font-medium ${currentPage===1?"border-slate-100 text-slate-300 cursor-not-allowed":"border-slate-200 text-slate-600 hover:bg-slate-50"}`} whileTap={{scale:0.92}}>‹</motion.button>
              {Array.from({length:totalPages},(_,i)=>i+1).filter(p=>totalPages<=5||p===1||p===totalPages||Math.abs(p-currentPage)<=1).reduce((acc,p,idx,arr)=>{if(idx>0&&p-arr[idx-1]>1)acc.push('...');acc.push(p);return acc;},[]).map((item,idx)=>
                item==='...'
                  ? <span key={`e-${idx}`} className="w-8 h-8 flex items-center justify-center text-slate-300 text-xs">…</span>
                  : <motion.button key={item} onClick={() => setCurrentPage(item)} className={`w-8 h-8 flex items-center justify-center rounded-xl border text-[12px] font-semibold ${currentPage===item?"bg-slate-800 border-slate-800 text-white shadow-sm":"border-slate-200 text-slate-500 hover:bg-slate-50"}`} whileTap={{scale:0.92}}>{item}</motion.button>
              )}
              <motion.button onClick={() => setCurrentPage(p => Math.min(totalPages, p+1))} disabled={currentPage===totalPages} className={`w-8 h-8 flex items-center justify-center rounded-xl border text-sm font-medium ${currentPage===totalPages?"border-slate-100 text-slate-300 cursor-not-allowed":"border-slate-200 text-slate-600 hover:bg-slate-50"}`} whileTap={{scale:0.92}}>›</motion.button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ManagerCompOffRequests;